import { Card, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import CodeViewer from "@/components/ui/code-viewer";
import { memo, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Spinner } from "../ui/spinner";
import { toast } from "sonner";
import ResultsPopover from "../Common/ResultsPopover";

interface ResultsPanelProps {
  title: string;
  description?: string | ReactNode;
  isLoading?: boolean;
  error?: unknown;
  responseJSON?: string;
  isEmptyResults?: boolean;
  onCopyCode: () => void;
  handleExportCSV: () => void;
  handleExportJSON: () => void;
  additionalHeaderContent?: ReactNode;
}

function ResultsPanel({
  title,
  description,
  isLoading,
  error,
  responseJSON,
  isEmptyResults = false,
  onCopyCode,
  handleExportCSV,
  handleExportJSON,
  additionalHeaderContent,
}: ResultsPanelProps) {
  const hasToastedEmpty = useRef(false);

  useEffect(() => {
    if (isEmptyResults && !isLoading && !error) {
      if (!hasToastedEmpty.current) {
        hasToastedEmpty.current = true;
        toast.info("No results found");
      }
    } else {
      hasToastedEmpty.current = false;
    }
  }, [isEmptyResults, isLoading, error]);

  const disableActions = isEmptyResults || !responseJSON;
  // Changing this key causes React to remount the content div,
  // re-firing the CSS enter animation on every state transition.
  const contentKey = isLoading ? "loading" : error ? "error" : "content";

  return (
    <Card className="shadow-custom scrollbar-thin bg-dark-surface m-auto flex h-full max-w-full flex-col !p-0">
      <div className="card-header-left flex-shrink-0">
        <div className="flex w-full flex-col gap-4 px-5 pt-3 pb-0 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1 pb-3">
            <CardTitle className="text-brand-500">{title}</CardTitle>
            {description && !isLoading && (
              <CardDescription className="text-sm text-neutral-300">
                {typeof description === "string" ? description : description}
              </CardDescription>
            )}
          </div>

          <div className="flex flex-wrap gap-3 sm:flex-nowrap">
            {additionalHeaderContent}

            <ResultsPopover
              responseJSON={responseJSON || ""}
              handleExportCSV={handleExportCSV}
              handleExportJSON={handleExportJSON}
              disabled={isEmptyResults}
            />
            <Button
              variant="ghost"
              onClick={onCopyCode}
              disabled={disableActions}
              className="!p-0 text-lg font-medium text-white"
            >
              <i className="ri-file-copy-line text-md text-white"></i>
            </Button>
          </div>
        </div>
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-b-[16px]">
        <div key={contentKey} className="animate-content-enter h-full">
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-white">
              <div className="text-center">
                <Spinner size="lg" className="stroke-primary!" />
              </div>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center text-white">
              <div className="text-center">
                <p className="text-sm font-bold">Error loading data</p>
                <p className="text-xs text-neutral-300">
                  {error instanceof Error ? error.message : String(error)}
                </p>
              </div>
            </div>
          ) : (
            <div className="relative h-full w-full">
              <div className="h-full">
                <CodeViewer
                  value={responseJSON || "{}"}
                  language="json"
                  height="100%"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default memo(ResultsPanel);
